/**
 * EMAPivotTable - Data-Dense Multi-Timeframe EMA Matrix
 *
 * Displays EMA trend data in compact pivot-table format.
 * Shows H4 and D1 timeframes with EMA60, EMA100, bars count, trend direction.
 * Maximum data density with minimal vertical space.
 *
 * PHASE 3.1: Uses QAI Design Tokens for institutional-grade typography
 */

import React, { memo } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import {
    QAI_TYPE_SCALE,
    QAI_FONTS,
    QAI_TRACKING,
    QAI_LAYOUT,
    QAI_COLORS
} from '../../../theme/qaiDesignTokens';

/**
 * Get trend color
 */
const getTrendColor = (position, bars) => {
    if (!position) return '#6b7280';
    const isAbove = position?.toUpperCase() === 'ABOVE';
    const strength = bars > 10 ? 'strong' : bars > 5 ? 'moderate' : 'weak';

    if (isAbove) {
        return strength === 'strong' ? '#22c55e' : strength === 'moderate' ? '#4ade80' : '#86efac';
    } else {
        return strength === 'strong' ? '#ef4444' : strength === 'moderate' ? '#f87171' : '#fca5a5';
    }
};

/**
 * Get direction color
 */
const getDirectionColor = (direction) => {
    if (!direction) return '#6b7280';
    const dir = direction.toUpperCase();
    if (dir.includes('STRONG') && dir.includes('UP')) return '#22c55e';
    if (dir.includes('UP')) return '#4ade80';
    if (dir.includes('STRONG') && dir.includes('DOWN')) return '#ef4444';
    if (dir.includes('DOWN')) return '#f87171';
    return '#fbbf24';
};

/**
 * Table Header Cell - PHASE 3.1: 11px UPPERCASE headers
 */
const HeaderCell = ({ children, width = 'auto', align = 'center' }) => (
    <Box
        sx={{
            width,
            minWidth: width !== 'auto' ? width : '45px',
            padding: QAI_LAYOUT.paddingCell,
            textAlign: align,
            borderRight: `1px solid ${QAI_COLORS.border.subtle}`,
            '&:last-child': { borderRight: 'none' }
        }}
    >
        <Typography
            sx={{
                fontSize: QAI_TYPE_SCALE.xs,  // 11px
                fontWeight: 600,
                color: QAI_COLORS.text.muted,
                fontFamily: QAI_FONTS.mono,
                textTransform: 'uppercase',
                letterSpacing: QAI_TRACKING.wide
            }}
        >
            {children}
        </Typography>
    </Box>
);

/**
 * Table Data Cell - PHASE 3.1: 13px data
 */
const DataCell = ({ children, color = '#fff', width = 'auto', align = 'center', bold = false }) => (
    <Box
        sx={{
            width,
            minWidth: width !== 'auto' ? width : '45px',
            padding: QAI_LAYOUT.paddingCell,
            textAlign: align,
            borderRight: `1px solid ${QAI_COLORS.border.subtle}`,
            '&:last-child': { borderRight: 'none' }
        }}
    >
        <Typography
            sx={{
                fontSize: QAI_TYPE_SCALE.sm,  // 13px
                fontWeight: bold ? 700 : 500,
                color,
                fontFamily: QAI_FONTS.mono,
                letterSpacing: QAI_TRACKING.tight
            }}
        >
            {children}
        </Typography>
    </Box>
);

/**
 * Format price based on value magnitude (forex vs indices vs crypto)
 */
const formatEmaPrice = (value) => {
    if (!value && value !== 0) return '--';
    // Auto-detect decimals: forex (5), indices (2), large numbers (0)
    if (value > 1000) return value.toFixed(2);      // Indices like XAUUSD, US30
    if (value > 10) return value.toFixed(4);         // Pairs like USDJPY
    return value.toFixed(5);                          // Standard forex pairs
};

/**
 * Position Arrow Indicator with Tooltip for full precision
 */
const PositionArrow = ({ position, value, label }) => {
    const isAbove = position?.toUpperCase() === 'ABOVE';
    const arrow = isAbove ? '▲' : '▼';
    const color = isAbove ? '#22c55e' : '#ef4444';
    const displayValue = formatEmaPrice(value);
    const fullValue = value ? value.toFixed(5) : '--';

    return (
        <Tooltip title={`${label}: ${fullValue}`} arrow placement="top">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, cursor: 'help' }}>
                <Typography sx={{ fontSize: '8px', color }}>{arrow}</Typography>
                <Typography sx={{ fontSize: '10px', color: '#b4bcd0', fontFamily: 'monospace', fontWeight: 600 }}>
                    {displayValue}
                </Typography>
            </Box>
        </Tooltip>
    );
};

/**
 * EMAPivotTable Component
 *
 * Props:
 * - data: From strategyDashboard.trendAnalysis (fallback)
 * - currentState: Real-time state with trendStatus
 * - trendFilterStats: Task 1.1 - Extended trend filter statistics
 */
const EMAPivotTable = memo(({ data, currentState, trendFilterStats }) => {
    // Extract trend data from currentState.trendStatus (API response structure)
    // Use trendFilterStats as enhancement when available (Task 1.1)
    const trendStatus = currentState?.trendStatus || {};
    const h4Ema60 = trendStatus.h4Ema60 || {};
    const h4Ema100 = trendStatus.h4Ema100 || {};
    const d1Ema60 = trendStatus.d1Ema60 || {};
    const d1Ema100 = trendStatus.d1Ema100 || {};

    // Task 1.1: Use trendFilterStats for enhanced bars-above data
    const h4Stats = trendFilterStats?.h4 || {};
    const d1Stats = trendFilterStats?.d1 || {};

    const combinedScore = currentState?.trendScore;
    const direction = currentState?.trendDescription || currentState?.overallTrend;

    const timeframes = [
        {
            tf: 'H4',
            ema60Pos: h4Ema60.position,
            ema60Val: h4Ema60.emaValue,
            ema100Pos: h4Ema100.position,
            ema100Val: h4Ema100.emaValue,
            // Task 1.1: Prefer trendFilterStats, fallback to trendStatus
            bars60: h4Stats.ema60BarsAbove ?? h4Ema60.barsAbove,
            bars100: h4Stats.ema100BarsAbove ?? h4Ema100.barsAbove,
            consecutive: h4Stats.consecutiveBarsAbove  // Task 1.1: New field
        },
        {
            tf: 'D1',
            ema60Pos: d1Ema60.position,
            ema60Val: d1Ema60.emaValue,
            ema100Pos: d1Ema100.position,
            ema100Val: d1Ema100.emaValue,
            // Task 1.1: Prefer trendFilterStats, fallback to trendStatus
            bars60: d1Stats.ema60BarsAbove ?? d1Ema60.barsAbove,
            bars100: d1Stats.ema100BarsAbove ?? d1Ema100.barsAbove,
            consecutive: d1Stats.consecutiveBarsAbove  // Task 1.1: New field
        }
    ];

    return (
        <Box sx={{
            backgroundColor: QAI_COLORS.background.primary,
            borderRadius: '4px',
            overflow: 'hidden',
            border: `1px solid ${QAI_COLORS.border.subtle}`
        }}>
            {/* Table Header - PHASE 3.1: 36px header */}
            <Box
                sx={{
                    display: 'flex',
                    height: QAI_LAYOUT.rowNormal,  // 36px
                    backgroundColor: QAI_COLORS.semantic.dangerMuted,
                    borderBottom: `1px solid ${QAI_COLORS.semantic.dangerBorder}`
                }}
            >
                <HeaderCell width="35px">TF</HeaderCell>
                <HeaderCell width="55px">EMA60</HeaderCell>
                <HeaderCell width="55px">EMA100</HeaderCell>
                <HeaderCell width="40px">B60</HeaderCell>
                <HeaderCell width="40px">B100</HeaderCell>
                <HeaderCell width="70px">TREND</HeaderCell>
            </Box>

            {/* Table Body - PHASE 3.1: 32px compact rows */}
            {timeframes.map((row, index) => (
                <Box
                    key={row.tf}
                    sx={{
                        display: 'flex',
                        height: QAI_LAYOUT.rowCompact,  // 32px
                        alignItems: 'center',
                        borderBottom: index < timeframes.length - 1 ? `1px solid ${QAI_COLORS.border.subtle}` : 'none',
                        '&:hover': { backgroundColor: QAI_COLORS.background.surface2 }
                    }}
                >
                    <DataCell width="35px" color={QAI_COLORS.semantic.neutral} bold>{row.tf}</DataCell>
                    <Box sx={{
                        width: '55px',
                        padding: QAI_LAYOUT.paddingCell,
                        borderRight: `1px solid ${QAI_COLORS.border.subtle}`
                    }}>
                        <PositionArrow position={row.ema60Pos} value={row.ema60Val} label={`${row.tf} EMA60`} />
                    </Box>
                    <Box sx={{
                        width: '55px',
                        padding: QAI_LAYOUT.paddingCell,
                        borderRight: `1px solid ${QAI_COLORS.border.subtle}`
                    }}>
                        <PositionArrow position={row.ema100Pos} value={row.ema100Val} label={`${row.tf} EMA100`} />
                    </Box>
                    <DataCell width="40px" color={getTrendColor(row.ema60Pos, row.bars60)}>
                        {row.bars60 ?? '--'}
                    </DataCell>
                    <DataCell width="40px" color={getTrendColor(row.ema100Pos, row.bars100)}>
                        {row.bars100 ?? '--'}
                    </DataCell>
                    <DataCell width="70px" color={getDirectionColor(direction)} bold>
                        {row.tf === 'H4' ? (direction?.replace('_', ' ') || '--') : '--'}
                    </DataCell>
                </Box>
            ))}

            {/* Summary Row - PHASE 3.1 */}
            <Box
                sx={{
                    display: 'flex',
                    backgroundColor: QAI_COLORS.background.surface2,
                    borderTop: `1px solid ${QAI_COLORS.border.default}`
                }}
            >
                <Box sx={{ flex: 1, padding: QAI_LAYOUT.paddingCell, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{
                        fontSize: QAI_TYPE_SCALE['2xs'],  // 10px
                        color: QAI_COLORS.text.muted,
                        fontFamily: QAI_FONTS.mono,
                        letterSpacing: QAI_TRACKING.wide
                    }}>
                        COMBINED:
                    </Typography>
                    <Typography sx={{
                        fontSize: QAI_TYPE_SCALE.sm,  // 13px
                        color: getDirectionColor(direction),
                        fontWeight: 700,
                        fontFamily: QAI_FONTS.mono
                    }}>
                        {direction?.replace('_', ' ') || 'UNKNOWN'}
                    </Typography>
                </Box>
                <Box sx={{ padding: QAI_LAYOUT.paddingCell, display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{
                        fontSize: QAI_TYPE_SCALE.sm,  // 13px
                        color: getDirectionColor(direction),
                        fontWeight: 700,
                        fontFamily: QAI_FONTS.mono,
                        letterSpacing: QAI_TRACKING.tight
                    }}>
                        {combinedScore ?? '--'}/100
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
});

EMAPivotTable.displayName = 'EMAPivotTable';

export default EMAPivotTable;
